import { inject } from '@angular/core';
import { map, switchMap, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';

import {
  fetchAllPages,
  withCursorPagination,
  withPickerList,
} from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { BillingBillingItemBundleService } from './generated/billing-billing-item-bundle/billing-billing-item-bundle.service';
import type {
  BillingItemBundleCreateRequestV1Request,
  BillingItemBundleLineAddRequestV1Request,
  BillingItemBundleResponseV1,
  BillingItemBundleV1,
  BillingV1BillingItemBundlesListParams,
  PatchedBillingItemBundleLineUpdateRequestV1Request,
  PatchedBillingItemBundleUpdateRequestV1Request,
} from './generated/types';

/**
 * Bundle = named preset of BillingItems the front expands into
 * `SetupChargeLines` when composing a SetupCharge (the backend does NOT
 * expand — see the schema comment). Same shape as `SetupChargesStore`:
 * cursor + CRUD of the entity + CRUD of the child `lines/` sub-resource.
 * Each line mutation answers with the WHOLE parent bundle so the entity
 * slice stays in sync without a separate lines store — DELETE is the only
 * mutation that cannot hand us the bundle (204) and refetches once.
 *
 * The `allBundles` picker list mirrors `allBillingItems` on
 * `BillingItemsStore`: a dropdown needs the full inventory, never the
 * cursor pages.
 */
export const BillingItemBundlesStore = signalStore(
  withEntities<BillingItemBundleV1>(),
  withCursorPagination<
    BillingItemBundleV1,
    BillingV1BillingItemBundlesListParams
  >(() => {
    const api = inject(BillingBillingItemBundleService);
    // The generated page already is { results, next, previous }: no adapter.
    return (query, cursor) =>
      api.billingV1BillingItemBundlesList({ ...query, cursor: cursor ?? undefined });
  }),
  withPickerList('bundles', () => {
    const api = inject(BillingBillingItemBundleService);
    return () =>
      fetchAllPages((cursor) =>
        api.billingV1BillingItemBundlesList({ active: true, page_size: 1000, cursor }),
      );
  }),
  withMethods((store, api = inject(BillingBillingItemBundleService)) => {
    // Single-bundle endpoints answer `{ billing_item_bundle }` (same wrapper as
    // `BillingItemResponseV1`); consumers keep getting the bundle itself.
    const unwrap = (res: BillingItemBundleResponseV1): BillingItemBundleV1 =>
      res.billing_item_bundle;
    const syncBundle = (bundle: BillingItemBundleV1): void => {
      patchState(store, updateEntity({ id: bundle.id, changes: bundle }));
    };

    return {
      createBundle(body: BillingItemBundleCreateRequestV1Request) {
        return api.billingV1BillingItemBundlesCreate(body).pipe(
          tap(() => {
            store.load({});
          }),
          map(unwrap),
        );
      },

      updateBundle(
        identity: string,
        body: PatchedBillingItemBundleUpdateRequestV1Request,
      ) {
        return api
          .billingV1BillingItemBundlesPartialUpdate({ identity }, body)
          .pipe(map(unwrap), tap(syncBundle));
      },

      deleteBundle(identity: string, id: number) {
        return api.billingV1BillingItemBundlesDestroy({ identity }).pipe(
          tap(() => {
            patchState(store, removeEntity(id));
          }),
        );
      },

      addBundleLine(
        identity: string,
        body: BillingItemBundleLineAddRequestV1Request,
      ) {
        return api
          .billingV1BillingItemBundlesLinesCreate({ identity }, body)
          .pipe(map(unwrap), tap(syncBundle));
      },

      updateBundleLine(
        bundleIdentity: string,
        lineIdentity: string,
        body: PatchedBillingItemBundleLineUpdateRequestV1Request,
      ) {
        return api
          .billingV1BillingItemBundlesLinesPartialUpdate(
            { identity: bundleIdentity, lineIdentity },
            body,
          )
          .pipe(map(unwrap), tap(syncBundle));
      },

      deleteBundleLine(bundleIdentity: string, lineIdentity: string) {
        return api
          .billingV1BillingItemBundlesLinesDestroy({
            identity: bundleIdentity,
            lineIdentity,
          })
          .pipe(
            switchMap(() =>
              api
                .billingV1BillingItemBundlesRetrieve({ identity: bundleIdentity })
                .pipe(map(unwrap), tap(syncBundle)),
            ),
          );
      },
    };
  }),
);
